import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Language, languageLabels } from './types';
import { translations } from './translations';
import { SiteLanguage, getLanguageFromPath, stripLocale, localizePath, hasSkVersion } from './routing';

// Define the language context type
type LanguageContextType = {
  language: SiteLanguage;
  setLanguage: (language: SiteLanguage) => void;
  t: (key: keyof typeof translations.en) => string;
  localizePath: (to: string) => string;
  // true when the current page has both an English and a Slovak version
  isBilingualPage: boolean;
};

// Create the context with default values
const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: (key) => '',
  localizePath: (to) => to,
  isBilingualPage: false,
});

// Custom hook to use the language context
export const useLanguage = () => useContext(LanguageContext);

// Provider component — must sit inside <BrowserRouter>, the language comes from the URL
export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const language = getLanguageFromPath(location.pathname);
  const basePath = stripLocale(location.pathname);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Translation function — falls back to English when a Slovak string is missing
  const t = (key: keyof typeof translations.en): string => {
    return translations[language][key] || translations.en[key] || key;
  };

  const setLanguage = (next: SiteLanguage) => {
    if (next === language) return;
    const target = next === 'sk' ? localizePath(basePath, 'sk') : basePath;
    navigate(`${target}${location.search}${location.hash}`);
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        t,
        localizePath: (to) => localizePath(to, language),
        isBilingualPage: hasSkVersion(basePath),
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

// Re-export types and constants for easier imports - fix TS1205 error by using export type
export type { Language, SiteLanguage };
export { languageLabels, translations };
